import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
  Box,
  Grid,
  CircularProgress,
  makeStyles,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  InputLabel
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  Save as SaveIcon,
  Send as SendIcon,
  Email as EmailIcon,
  Message as WhatsAppIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from "@material-ui/icons";
import useVPSNotifications from "../hooks/useVPSNotifications";
import useSMTPConfig from "../hooks/useSMTPConfig";
import useEmailTemplates from "../hooks/useEmailTemplates";
import { WhatsAppsContext } from "../../../context/WhatsApp/WhatsAppsContext";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    minHeight: "100vh"
  },
  card: {
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.mode === 'dark' 
      ? '0 2px 8px rgba(0,0,0,0.3)' 
      : '0 2px 8px rgba(0,0,0,0.1)'
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.text.primary
  },
  infoBox: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.mode === 'dark' 
      ? 'rgba(33, 150, 243, 0.1)' 
      : 'rgba(33, 150, 243, 0.05)',
    borderRadius: 8,
    border: `1px solid ${theme.mode === 'dark' ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.2)'}`
  },
  warningBox: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.mode === 'dark' 
      ? 'rgba(255, 152, 0, 0.1)' 
      : 'rgba(255, 152, 0, 0.05)',
    borderRadius: 8,
    border: `1px solid ${theme.mode === 'dark' ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.2)'}`
  },
  formField: {
    marginBottom: theme.spacing(2)
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "flex-end",
    marginTop: theme.spacing(3)
  },
  testButton: {
    marginTop: theme.spacing(1)
  },
  testModal: {
    "& .MuiDialog-paper": {
      backgroundColor: theme.palette.background.paper
    }
  },
  testResult: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    borderRadius: 8
  }
}));

const NotificationAlertsConfig = () => {
  const classes = useStyles();
  const {
    loading,
    config,
    sendingTest,
    saveConfig,
    sendTestNotification
  } = useVPSNotifications();

  // Hooks para obtener datos de SMTP, plantillas y WhatsApp
  const { configs: smtpConfigs } = useSMTPConfig();
  const { templates: emailTemplates } = useEmailTemplates();
  const { whatsApps } = useContext(WhatsAppsContext);

  // Estados del formulario
  const [formData, setFormData] = useState({
    enabled: false,
    notificationType: 'email',
    emailEnabled: false,
    smtpConfigId: null,
    emailTemplateId: null,
    adminEmail: '',
    whatsappEnabled: false,
    whatsappId: null,
    whatsappMessageTemplate: `⚠️ ALERTA VPS: {alertType}
📊 Estado del Sistema:
💾 Disco: {diskUsage}%
📝 Binary Logs: {binaryLogsSize} GB
⚡ CPU: {cpuUsage}%
🧠 Memoria: {memoryUsage}%

{message}`,
    adminWhatsapp: '',
    enableDiskAlerts: true,
    enableBinaryLogAlerts: true,
    enableCpuAlerts: true,
    enableMemoryAlerts: true,
    enableCriticalAlerts: true,
    diskAlertThreshold: 75,
    binaryLogAlertThreshold: 50.00,
    cpuAlertThreshold: 80,
    memoryAlertThreshold: 85,
    enableAutoCleanup: false,
    autoCleanupDiskThreshold: 75,
    alertFrequency: 6
  });

  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [infoModalContent, setInfoModalContent] = useState({ title: '', message: '', type: 'info' }); // 'info' | 'success' | 'error' | 'warning'

  // Función helper para convertir valores a boolean
  const toBoolean = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') return value === 'true' || value === '1';
    return Boolean(value);
  };

  // Cargar configuración cuando esté disponible
  useEffect(() => {
    if (config) {
      // Buscar plantilla unificada VPS por defecto
      const defaultVpsTemplate = emailTemplates.find(
        t => t.purpose === 'vps_alert' && t.name && t.name.includes('Completa')
      );
      
      // Plantilla WhatsApp por defecto
      const defaultWhatsAppTemplate = `⚠️ ALERTA VPS: {alertType}
📊 Estado del Sistema:
💾 Disco: {diskUsage}%
📝 Binary Logs: {binaryLogsSize} GB
⚡ CPU: {cpuUsage}%
🧠 Memoria: {memoryUsage}%

{message}`;
      
      // Si no hay plantilla configurada, usar la unificada por defecto
      const emailTemplateId = config.emailTemplateId || (defaultVpsTemplate ? defaultVpsTemplate.id : null);
      
      // Si no hay plantilla WhatsApp configurada, usar la por defecto
      const whatsappTemplate = config.whatsappMessageTemplate || defaultWhatsAppTemplate;

      setFormData(prev => ({
        ...prev,
        enabled: toBoolean(config.enabled),
        notificationType: config.notificationType || 'email',
        emailEnabled: toBoolean(config.emailEnabled),
        smtpConfigId: config.smtpConfigId || null,
        emailTemplateId: emailTemplateId,
        adminEmail: config.adminEmail || '',
        whatsappEnabled: toBoolean(config.whatsappEnabled),
        whatsappId: config.whatsappId || null,
        whatsappMessageTemplate: whatsappTemplate,
        adminWhatsapp: config.adminWhatsapp || '',
        enableDiskAlerts: config.enableDiskAlerts !== undefined ? toBoolean(config.enableDiskAlerts) : true,
        enableBinaryLogAlerts: config.enableBinaryLogAlerts !== undefined ? toBoolean(config.enableBinaryLogAlerts) : true,
        enableCpuAlerts: config.enableCpuAlerts !== undefined ? toBoolean(config.enableCpuAlerts) : true,
        enableMemoryAlerts: config.enableMemoryAlerts !== undefined ? toBoolean(config.enableMemoryAlerts) : true,
        enableCriticalAlerts: config.enableCriticalAlerts !== undefined ? toBoolean(config.enableCriticalAlerts) : true,
        diskAlertThreshold: config.diskAlertThreshold || 75,
        binaryLogAlertThreshold: config.binaryLogAlertThreshold || 50.00,
        cpuAlertThreshold: config.cpuAlertThreshold || 80,
        memoryAlertThreshold: config.memoryAlertThreshold || 85,
        enableAutoCleanup: toBoolean(config.enableAutoCleanup),
        autoCleanupDiskThreshold: config.autoCleanupDiskThreshold || 75,
        alertFrequency: config.alertFrequency || 6
      }));
    }
  }, [config, emailTemplates]);

  /**
   * Manejar cambios en campos del formulario
   */
  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /**
   * Manejar cambio de tipo de notificación
   */
  const handleNotificationTypeChange = (e) => {
    const type = e.target.value;
    setFormData(prev => ({
      ...prev,
      notificationType: type,
      emailEnabled: type === 'email' || type === 'both',
      whatsappEnabled: type === 'whatsapp' || type === 'both'
    }));
  };

  /**
   * Mostrar modal informativo
   */
  const showInfoModal = (title, message, type = 'info') => {
    setInfoModalContent({ title, message, type });
    setInfoModalOpen(true);
  };

  /**
   * Enviar notificación de prueba
   * Permite enviar prueba incluso si el sistema no está habilitado completamente
   */
  const handleSendTestNotification = async () => {
    // Verificar que al menos un método esté configurado
    if (!formData.smtpConfigId && !formData.whatsappId) {
      showInfoModal(
        "⚠️ Configuración Requerida",
        "Por favor configura al menos un método de notificación (Email o WhatsApp) antes de enviar una prueba.",
        'warning'
      );
      return;
    }

    // Verificar email si está seleccionado
    if ((formData.notificationType === 'email' || formData.notificationType === 'both') && !formData.smtpConfigId) {
      showInfoModal(
        "⚠️ Configuración SMTP Requerida",
        "Por favor selecciona una configuración SMTP antes de enviar una prueba de email.",
        'warning'
      );
      return;
    }

    if ((formData.notificationType === 'email' || formData.notificationType === 'both') && !formData.adminEmail) {
      showInfoModal(
        "⚠️ Email del Administrador Requerido",
        "Por favor ingresa el email del administrador antes de enviar una prueba.",
        'warning'
      );
      return;
    }

    // Verificar WhatsApp si está seleccionado
    if ((formData.notificationType === 'whatsapp' || formData.notificationType === 'both') && !formData.whatsappId) {
      showInfoModal(
        "⚠️ Conexión WhatsApp Requerida",
        "Por favor selecciona una conexión WhatsApp antes de enviar una prueba.",
        'warning'
      );
      return;
    }

    if ((formData.notificationType === 'whatsapp' || formData.notificationType === 'both') && !formData.adminWhatsapp) {
      showInfoModal(
        "⚠️ Número de WhatsApp Requerido",
        "Por favor ingresa el número de WhatsApp del administrador antes de enviar una prueba.",
        'warning'
      );
      return;
    }

    // Abrir modal de carga
    setTestModalOpen(true);
    setTestResult(null);

    try {
      // Enviar datos del formulario para permitir pruebas sin guardar
      const result = await sendTestNotification({
        smtpConfigId: formData.smtpConfigId,
        adminEmail: formData.adminEmail,
        emailTemplateId: formData.emailTemplateId,
        whatsappId: formData.whatsappId,
        adminWhatsapp: formData.adminWhatsapp,
        whatsappMessageTemplate: formData.whatsappMessageTemplate,
        notificationType: formData.notificationType
      });
      
      if (result.success) {
        const sentMessages = [];
        if (result.sent?.email) sentMessages.push("✅ Email enviado exitosamente");
        if (result.sent?.whatsapp) sentMessages.push("✅ WhatsApp enviado exitosamente");
        
        setTestResult({
          success: true,
          message: `Notificación de prueba enviada exitosamente:\n\n${sentMessages.join('\n')}\n\nRevisa tu ${result.sent?.email ? 'email' : 'WhatsApp'} para confirmar la recepción.`
        });
      } else {
        setTestResult({
          success: false,
          message: `Error al enviar notificación de prueba:\n\n${result.errors?.join('\n') || result.message || 'Error desconocido'}`
        });
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: `Error al enviar notificación de prueba:\n\n${error.message || 'Error desconocido'}`
      });
    }
  };

  /**
   * Guardar configuración
   */
  const handleSave = async () => {
    // Validar que si está habilitado, tenga al menos un método configurado
    if (formData.enabled) {
      if (!formData.smtpConfigId && !formData.whatsappId) {
        showInfoModal(
          "⚠️ Configuración Incompleta",
          "Si el sistema está habilitado, debes configurar al menos un método de notificación (Email o WhatsApp).",
          'warning'
        );
        return;
      }

      // Validar email si está seleccionado
      if ((formData.notificationType === 'email' || formData.notificationType === 'both') && !formData.smtpConfigId) {
        showInfoModal(
          "⚠️ Configuración SMTP Requerida",
          "Debes seleccionar una configuración SMTP cuando las notificaciones por email están habilitadas.",
          'warning'
        );
        return;
      }

      if ((formData.notificationType === 'email' || formData.notificationType === 'both') && !formData.adminEmail) {
        showInfoModal(
          "⚠️ Email del Administrador Requerido",
          "Debes ingresar el email del administrador cuando las notificaciones por email están habilitadas.",
          'warning'
        );
        return;
      }

      // Validar WhatsApp si está seleccionado
      if ((formData.notificationType === 'whatsapp' || formData.notificationType === 'both') && !formData.whatsappId) {
        showInfoModal(
          "⚠️ Conexión WhatsApp Requerida",
          "Debes seleccionar una conexión WhatsApp cuando las notificaciones por WhatsApp están habilitadas.",
          'warning'
        );
        return;
      }

      if ((formData.notificationType === 'whatsapp' || formData.notificationType === 'both') && !formData.adminWhatsapp) {
        showInfoModal(
          "⚠️ Número de WhatsApp Requerido",
          "Debes ingresar el número de WhatsApp del administrador cuando las notificaciones por WhatsApp están habilitadas.",
          'warning'
        );
        return;
      }
    }

    try {
      await saveConfig(formData);
      showInfoModal(
        "✅ Configuración Guardada",
        "La configuración de notificaciones VPS se ha guardado exitosamente.",
        'success'
      );
    } catch (error) {
      console.error("Error guardando configuración:", error);
      showInfoModal(
        "❌ Error al Guardar",
        `Error al guardar la configuración: ${error.message || 'Error desconocido'}`,
        'error'
      );
    }
  };

  /**
   * Validación en tiempo real
   */
  const validateEmail = (email) => {
    if (!email) return true; // Campo opcional si no está habilitado
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateWhatsAppNumber = (number) => {
    if (!number) return true;
    return /^[+]?[0-9]{10,15}$/.test(number);
  };

  // Filtrar SMTP activos y WhatsApp conectados
  const activeSmtpConfigs = smtpConfigs.filter(c => c.isActive);
  const connectedWhatsApps = whatsApps.filter(w => w.status === 'CONNECTED');

  if (loading && !config) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className={classes.container}>
      {/* Sección 1: Habilitación General */}
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            Habilitación General
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={formData.enabled}
                onChange={handleChange('enabled')}
                color="primary"
              />
            }
            label="Habilitar sistema de alertas y limpieza automática"
          />

          {/* Info Box */}
          <Box className={classes.infoBox} mt={2}>
            <Box display="flex" alignItems="center" mb={1}>
              <InfoIcon style={{ marginRight: 8, color: '#2196F3' }} />
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                ¿Por qué habilitar este sistema?
              </Typography>
            </Box>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              Este sistema de alertas y limpieza automática es especialmente útil si:
            </Typography>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>Tu negocio maneja miles de mensajes WhatsApp diarios</li>
              <li>Generas muchos registros en la base de datos</li>
              <li>Necesitas monitoreo proactivo del servidor</li>
              <li>Quieres prevenir problemas de disco lleno</li>
            </ul>
            <Typography variant="body2" style={{ marginTop: 8, fontStyle: 'italic' }}>
              Si tu negocio es pequeño y no genera muchos registros, puedes mantener este sistema deshabilitado sin problemas.
            </Typography>
          </Box>

          {/* Warning Box */}
          {formData.enabled && (
            <Box className={classes.warningBox} mt={2}>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                <span role="img" aria-label="warning">⚠️</span> IMPORTANTE: Una vez habilitado, recibirás alertas automáticas cuando el sistema detecte problemas. 
                Asegúrate de configurar correctamente tus credenciales de email o WhatsApp.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Sección 2: Configuración de Notificaciones (solo si enabled) */}
      {formData.enabled && (
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle}>
              Configuración de Notificaciones
            </Typography>

            <FormControl component="fieldset" className={classes.formField}>
              <RadioGroup
                row
                value={formData.notificationType}
                onChange={handleNotificationTypeChange}
              >
                <FormControlLabel value="email" control={<Radio />} label="Email" />
                <FormControlLabel value="whatsapp" control={<Radio />} label="WhatsApp" />
                <FormControlLabel value="both" control={<Radio />} label="Ambos" />
              </RadioGroup>
            </FormControl>

            <Divider style={{ margin: '16px 0' }} />

            {/* Formulario Email */}
            {(formData.notificationType === 'email' || formData.notificationType === 'both') && (
              <Box mb={3}>
                <Typography variant="subtitle1" style={{ marginBottom: 16, fontWeight: 600 }}>
                  <EmailIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                  Configuración Email
                </Typography>

                <FormControl fullWidth className={classes.formField}>
                  <InputLabel>Configuración SMTP *</InputLabel>
                  <Select
                    value={formData.smtpConfigId || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, smtpConfigId: e.target.value || null }))}
                    label="Configuración SMTP *"
                  >
                    <MenuItem value="">
                      <em>Seleccionar SMTP...</em>
                    </MenuItem>
                    {activeSmtpConfigs.map((smtp) => (
                      <MenuItem key={smtp.id} value={smtp.id}>
                        {smtp.name} {smtp.isDefault && '(Por defecto)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth className={classes.formField}>
                  <InputLabel>Plantilla de Email (Opcional)</InputLabel>
                  <Select
                    value={formData.emailTemplateId || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, emailTemplateId: e.target.value || null }))}
                    label="Plantilla de Email (Opcional)"
                  >
                    <MenuItem value="">
                      <em>Usar plantillas por defecto</em>
                    </MenuItem>
                    {emailTemplates
                      .filter(template => 
                        template.purpose && 
                        (template.purpose === 'vps_alert' || template.purpose.startsWith('vps_alert'))
                      )
                      .map((template) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name} - {template.purpose}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Email del Administrador *"
                  value={formData.adminEmail}
                  onChange={handleChange('adminEmail')}
                  className={classes.formField}
                  error={!!(formData.adminEmail && !validateEmail(formData.adminEmail))}
                  helperText={formData.adminEmail && !validateEmail(formData.adminEmail) ? "Formato de email inválido" : "Email donde recibirás las alertas"}
                />
              </Box>
            )}

            {/* Formulario WhatsApp */}
            {(formData.notificationType === 'whatsapp' || formData.notificationType === 'both') && (
              <Box>
                <Typography variant="subtitle1" style={{ marginBottom: 16, fontWeight: 600 }}>
                  <WhatsAppIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                  Configuración WhatsApp
                </Typography>

                <FormControl fullWidth className={classes.formField}>
                  <InputLabel>Conexión WhatsApp *</InputLabel>
                  <Select
                    value={formData.whatsappId || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, whatsappId: e.target.value || null }))}
                    label="Conexión WhatsApp *"
                  >
                    <MenuItem value="">
                      <em>Seleccionar WhatsApp...</em>
                    </MenuItem>
                    {connectedWhatsApps.map((whatsapp) => (
                      <MenuItem key={whatsapp.id} value={whatsapp.id}>
                        {whatsapp.name} ({whatsapp.number})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Plantilla de Mensaje WhatsApp"
                  value={formData.whatsappMessageTemplate}
                  onChange={handleChange('whatsappMessageTemplate')}
                  className={classes.formField}
                  helperText="Variables disponibles: {alertType}, {diskUsage}, {binaryLogsSize}, {cpuUsage}, {memoryUsage}, {message}, {subject}"
                />

                <TextField
                  fullWidth
                  label="Número de WhatsApp del Administrador *"
                  value={formData.adminWhatsapp}
                  onChange={handleChange('adminWhatsapp')}
                  className={classes.formField}
                  placeholder="+1234567890"
                  error={!!(formData.adminWhatsapp && !validateWhatsAppNumber(formData.adminWhatsapp))}
                  helperText={formData.adminWhatsapp && !validateWhatsAppNumber(formData.adminWhatsapp) ? "Formato inválido (10-15 dígitos, opcionalmente precedido por +)" : "Número donde recibirás las alertas (con código de país)"}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sección 3: Umbrales de Alertas (solo si enabled) */}
      {formData.enabled && (
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle}>
              Umbrales de Alertas
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Disco (%)"
                  value={formData.diskAlertThreshold}
                  onChange={handleChange('diskAlertThreshold')}
                  inputProps={{ min: 0, max: 100 }}
                  helperText="Alerta cuando el uso de disco supere este porcentaje"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Binary Logs (GB)"
                  value={formData.binaryLogAlertThreshold}
                  onChange={handleChange('binaryLogAlertThreshold')}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Alerta cuando el tamaño de binary logs supere este valor"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="CPU (%)"
                  value={formData.cpuAlertThreshold}
                  onChange={handleChange('cpuAlertThreshold')}
                  inputProps={{ min: 0, max: 100 }}
                  helperText="Alerta cuando el uso de CPU supere este porcentaje"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Memoria (%)"
                  value={formData.memoryAlertThreshold}
                  onChange={handleChange('memoryAlertThreshold')}
                  inputProps={{ min: 0, max: 100 }}
                  helperText="Alerta cuando el uso de memoria supere este porcentaje"
                />
              </Grid>
            </Grid>

            <Box mt={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableDiskAlerts}
                    onChange={handleChange('enableDiskAlerts')}
                    color="primary"
                  />
                }
                label="Habilitar alertas de disco"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableBinaryLogAlerts}
                    onChange={handleChange('enableBinaryLogAlerts')}
                    color="primary"
                  />
                }
                label="Habilitar alertas de binary logs"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableCpuAlerts}
                    onChange={handleChange('enableCpuAlerts')}
                    color="primary"
                  />
                }
                label="Habilitar alertas de CPU"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableMemoryAlerts}
                    onChange={handleChange('enableMemoryAlerts')}
                    color="primary"
                  />
                }
                label="Habilitar alertas de memoria"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableCriticalAlerts}
                    onChange={handleChange('enableCriticalAlerts')}
                    color="primary"
                  />
                }
                label="Habilitar alertas críticas"
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Sección 4: Frecuencia de Notificaciones y Limpieza Automática (solo si enabled) */}
      {formData.enabled && (
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle}>
              Frecuencia de Notificaciones y Limpieza Automática
            </Typography>

            <Grid container spacing={2} style={{ marginBottom: 16 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Frecuencia de Alertas (horas)"
                  value={formData.alertFrequency}
                  onChange={handleChange('alertFrequency')}
                  inputProps={{ min: 1, max: 48 }}
                  helperText="Cada cuántas horas se enviarán alertas automáticas (1-48 horas)"
                />
              </Grid>
            </Grid>

            <Divider style={{ margin: '16px 0' }} />

            <Typography variant="subtitle1" style={{ marginBottom: 16, fontWeight: 600 }}>
              Limpieza Automática
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.enableAutoCleanup}
                  onChange={handleChange('enableAutoCleanup')}
                  color="primary"
                />
              }
              label="Habilitar limpieza automática"
            />

            {formData.enableAutoCleanup && (
              <Grid container spacing={2} style={{ marginTop: 8 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Umbral Disco para Limpieza (%)"
                    value={formData.autoCleanupDiskThreshold}
                    onChange={handleChange('autoCleanupDiskThreshold')}
                    inputProps={{ min: 0, max: 100 }}
                    helperText="Ejecutar limpieza automática cuando el disco supere este porcentaje. El sistema purgará binary logs y optimizará tablas de forma iterativa hasta que el disco baje del umbral."
                  />
                </Grid>
              </Grid>
            )}

            {/* Botones de Acción - Solo visibles cuando el sistema está habilitado */}
            <Box className={classes.actions} style={{ marginTop: 24 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {loading ? "Guardando..." : "Guardar Configuración"}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleSendTestNotification}
                disabled={sendingTest || (!formData.smtpConfigId && !formData.whatsappId)}
                startIcon={sendingTest ? <CircularProgress size={20} /> : <SendIcon />}
              >
                {sendingTest ? "Enviando..." : "Enviar Notificación de Prueba"}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Modal de Resultado de Prueba de Notificación */}
      <Dialog
        open={testModalOpen}
        onClose={() => {}} // No permitir cerrar con click fuera o ESC durante la prueba
        className={classes.testModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            {!testResult ? (
              <>
                <CircularProgress size={24} style={{ marginRight: 12 }} />
                <span>Enviando Notificación de Prueba...</span>
              </>
            ) : (
              <>
                {testResult.success ? (
                  <CheckCircleIcon style={{ color: '#4caf50', marginRight: 12 }} />
                ) : (
                  <CancelIcon style={{ color: '#f44336', marginRight: 12 }} />
                )}
                <span>
                  {testResult.success ? '✅ Notificación Enviada' : '❌ Error al Enviar'}
                </span>
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {testResult ? (
            <Box>
              <Alert
                severity={testResult.success ? "success" : "error"}
                className={classes.testResult}
                icon={testResult.success ? <CheckCircleIcon /> : <CancelIcon />}
              >
                <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                  {testResult.message}
                </Typography>
              </Alert>
              {testResult.success && (
                <Box mt={2} p={2} style={{ backgroundColor: 'rgba(76, 175, 80, 0.1)', borderRadius: 8 }}>
                  <Typography variant="body2" style={{ color: '#4caf50' }}>
                    <strong><span role="img" aria-label="lightbulb">💡</span> Tip:</strong> Revisa tu {formData.notificationType === 'email' || formData.notificationType === 'both' ? 'email' : ''} {formData.notificationType === 'both' ? 'y ' : ''} {formData.notificationType === 'whatsapp' || formData.notificationType === 'both' ? 'WhatsApp' : ''} para confirmar la recepción.
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" alignItems="center" py={3}>
              <CircularProgress size={48} />
              <Typography variant="body1" style={{ marginTop: 16 }}>
                Por favor espera mientras se envía la notificación...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setTestModalOpen(false)} 
            color="primary"
            variant="contained"
            disabled={!testResult} // Solo permitir cerrar cuando haya resultado
          >
            {testResult ? "Cerrar" : "Cancelar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Informativo General */}
      <Dialog
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        className={classes.testModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            {infoModalContent.type === 'success' && <CheckCircleIcon style={{ color: '#4caf50', marginRight: 12 }} />}
            {infoModalContent.type === 'error' && <CancelIcon style={{ color: '#f44336', marginRight: 12 }} />}
            {infoModalContent.type === 'warning' && <InfoIcon style={{ color: '#ff9800', marginRight: 12 }} />}
            {infoModalContent.type === 'info' && <InfoIcon style={{ color: '#2196f3', marginRight: 12 }} />}
            <span>{infoModalContent.title}</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
            {infoModalContent.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setInfoModalOpen(false)} 
            color="primary"
            variant="contained"
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default NotificationAlertsConfig;
